import React from 'react'
import styled from 'styled-components'
import { screenSize } from 'styles/shared'

import CollapsibleCard from 'components/CollapsibleCard'
import FieldExplanation from 'components/FieldExplanation'

import { useFeaturesContext } from 'hooks/FeaturesContext'
import AlertNotificationCheckboxList from 'pages/shared/NotificationRecipientForm/AlertEmailSubscriptionCard/AlertNotificationCheckboxList'

const CRITICAL_ALERTS_DESCRIPTION = 'Critical alerts consist of issues where primary functionality has been lost and likely require prompt attention.'
const MAJOR_ALERTS_DESCRIPTION = 'Major alerts consist of issues where some functionality has been lost or reduced and may need attention as soon as is convenient.'
const SDA_ALERTS_DESCRIPTION = 'Smart Diagnostics Alerts (SDA) are based on algorithms that run periodically in the cloud and are independent of the alerts triggered within the thermostat or HVAC equipment.  These alerts will not be displayed on the thermostat or in Home.'
const COMPANY_UPDATE_ALERTS_DESCRIPTION = 'Receive periodic company emails about things like new features or requests for feedback.'

interface Alerts {
  criticalAlerts: boolean
  majorAlerts: boolean
  criticalSdaAlerts: boolean
  majorSdaAlerts: boolean
  companyUpdateAlerts: boolean
  dailyDigestCriticalAlerts: boolean
  dailyDigestMajorAlerts: boolean
  dailyDigestCriticalSdaAlerts: boolean
  dailyDigestMajorSdaAlerts: boolean
}

interface Props {
  collapsedByDefault: boolean
  alerts: Alerts
  setAlerts: (alerts: Alerts) => void
}

interface AlertType {
  title: string
  checked: boolean
  description: string
  name: keyof Alerts
}

const AlertEmailSubscriptionCard = ({ collapsedByDefault, alerts, setAlerts }: Props) => {
  const { enabledFeatures } = useFeaturesContext()

  const individualAlertEmails: AlertType[] = [
    { title: 'Critical', checked: alerts.criticalAlerts, description: CRITICAL_ALERTS_DESCRIPTION, name: 'criticalAlerts' },
    { title: 'Major', checked: alerts.majorAlerts, description: MAJOR_ALERTS_DESCRIPTION, name: 'majorAlerts' },
    ...(enabledFeatures.includes('hehm') ? [
      { title: 'Critical SDA', checked: alerts.criticalSdaAlerts, description: SDA_ALERTS_DESCRIPTION, name: 'criticalSdaAlerts' as keyof Alerts }
    ] : []),
    { title: 'Major SDA', checked: alerts.majorSdaAlerts, description: SDA_ALERTS_DESCRIPTION, name: 'majorSdaAlerts' as keyof Alerts },
    { title: 'Company Update', checked: alerts.companyUpdateAlerts, description: COMPANY_UPDATE_ALERTS_DESCRIPTION, name: 'companyUpdateAlerts' }
  ]

  const dailyEmailDigest: AlertType[] = [
    { title: 'Critical', checked: alerts.dailyDigestCriticalAlerts, description: CRITICAL_ALERTS_DESCRIPTION, name: 'dailyDigestCriticalAlerts' },
    { title: 'Major', checked: alerts.dailyDigestMajorAlerts, description: MAJOR_ALERTS_DESCRIPTION, name: 'dailyDigestMajorAlerts' },
    ...(enabledFeatures.includes('hehm') ? [
      { title: 'Critical SDA', checked: alerts.dailyDigestCriticalSdaAlerts, description: SDA_ALERTS_DESCRIPTION, name: 'dailyDigestCriticalSdaAlerts' as keyof Alerts }
    ] : []),
    { title: 'Major SDA', checked: alerts.dailyDigestMajorSdaAlerts, description: SDA_ALERTS_DESCRIPTION, name: 'dailyDigestMajorSdaAlerts' as keyof Alerts }
  ]

  return (
    <CollapsibleCard title="Notification Subscription" collapsedByDefault={collapsedByDefault}>
      {enabledFeatures.includes('alert_digest')
        ? (
          <S.Container>
            <S.CheckList>
              <S.Header>
                <span>
                  Individual Customer Alert Emails
                  <FieldExplanation tooltipContent="Email for a single alert from a single system" />
                </span>
              </S.Header>
              <AlertNotificationCheckboxList alertTypes={individualAlertEmails} alerts={alerts} setAlerts={setAlerts} />
            </S.CheckList>

            <S.CheckList>
              <S.Header>
                <span>
                  Daily Email Digest - Consolidated Alerts
                  <FieldExplanation tooltipContent="A single email delivered once a day that consolidates all selected alert types from all systems" />
                </span>
              </S.Header>
              <AlertNotificationCheckboxList alertTypes={dailyEmailDigest} alerts={alerts} setAlerts={setAlerts} />
            </S.CheckList>
          </S.Container>)
        : (
          <AlertNotificationCheckboxList alertTypes={individualAlertEmails} alerts={alerts} setAlerts={setAlerts} />
        )
      }
    </CollapsibleCard>
  )
}

export default AlertEmailSubscriptionCard

const S = {
  Container: styled.div`
    display: flex;
    flex-direction: row;
    gap: 8%;
    padding-left: 5px;

    @media (${screenSize.small}) {
      flex-direction: column;
      gap: 10px;
    }
  `,
  CheckList: styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
  `,
  Header: styled.div`
    display: inline-block;
  `
}
